import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  communicationAPI, 
  Conversation, 
  ConversationWithMessages, 
  ConversationMessage,
  CommunicationStats,
  AnnouncementData 
} from '../../services/api';

type TabType = 'conversations' | 'announcements' | 'broadcast' | 'statistics';
type ParticipantType = 'dispatcher' | 'customer' | 'merchant';

interface UserOption {
  id: number;
  name: string;
  phone: string;
  email?: string;
  photo?: string;
  user_type: ParticipantType;
}

const AdminCommunicationHub: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>('conversations');
  const [stats, setStats] = useState<CommunicationStats | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ConversationWithMessages | null>(null);
  const [announcements, setAnnouncements] = useState<AnnouncementData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [selectedUserType, setSelectedUserType] = useState<ParticipantType>('dispatcher');
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [newSubject, setNewSubject] = useState('');
  const [initialMessage, setInitialMessage] = useState('');
  const userInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [conversationFilter, setConversationFilter] = useState<ParticipantType | 'all'>('all');
  
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastType, setBroadcastType] = useState('general');
  const [broadcastPriority, setBroadcastPriority] = useState('normal');
  const [targetAudience, setTargetAudience] = useState('all_dispatchers');
  const [sendPush, setSendPush] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [sendSms, setSendSms] = useState(false);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initializeHub = async () => {
      await loadData();
      await loadUsersByType('dispatcher');
      
      const tabParam = searchParams.get('tab') as TabType;
      if (tabParam && ['conversations', 'announcements', 'broadcast', 'statistics'].includes(tabParam)) {
        setActiveTab(tabParam);
      }
      
      const dispatcherIdParam = searchParams.get('dispatcher');
      if (dispatcherIdParam) {
        const userList = await loadUsersByType('dispatcher');
        const dispatcher = userList.find((d: UserOption) => String(d.id) === dispatcherIdParam);
        if (dispatcher) {
          setSelectedUser(dispatcher);
          setNewSubject(`Message for ${dispatcher.name}`);
          setShowNewConversation(true);
        }
      }
      
      setSearchParams({}, { replace: true });
    };
    
    initializeHub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      scrollToBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, conversationsData, announcementsData] = await Promise.all([
        communicationAPI.getStats(),
        communicationAPI.getConversations(),
        communicationAPI.getAnnouncementHistory()
      ]);
      setStats(statsData);
      setConversations(conversationsData);
      setAnnouncements(announcementsData.announcements);
    } catch (err: any) {
      setError(err.message || 'Failed to load communication data');
    } finally {
      setLoading(false);
    }
  };

  const loadUsersByType = async (userType: ParticipantType): Promise<UserOption[]> => {
    try {
      const data = await communicationAPI.getUsersByType(userType);
      const userList = data.map((u: any) => ({
        id: u.id,
        name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
        phone: u.phone || 'N/A',
        email: u.email,
        photo: u.photo,
        user_type: userType
      }));
      setUsers(userList);
      return userList;
    } catch (err) {
      console.error(`Failed to load ${userType}s:`, err);
      return [];
    }
  };

  const handleUserTypeChange = async (newType: ParticipantType) => {
    setSelectedUserType(newType);
    setSelectedUser(null);
    setUserSearch('');
    await loadUsersByType(newType);
  };

  const filteredUsers = users.filter(u => {
    const search = userSearch.toLowerCase();
    return u.name.toLowerCase().includes(search) || 
           u.phone.toLowerCase().includes(search) ||
           (u.email || '').toLowerCase().includes(search);
  });

  const handleUserSelect = (user: UserOption) => {
    setSelectedUser(user);
    setUserSearch('');
    setShowUserDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleUserKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showUserDropdown && e.key !== 'ArrowDown' && e.key !== 'Enter') return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!showUserDropdown) {
          setShowUserDropdown(true);
        } else {
          setHighlightedIndex(prev => 
            prev < filteredUsers.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredUsers[highlightedIndex]) {
          handleUserSelect(filteredUsers[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowUserDropdown(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleClearUser = () => {
    setSelectedUser(null);
    setUserSearch('');
    setHighlightedIndex(-1);
    userInputRef.current?.focus();
  };

  const getUserTypeLabel = (type: ParticipantType) => {
    switch (type) {
      case 'dispatcher': return 'Dispatcher';
      case 'customer': return 'Customer';
      case 'merchant': return 'Merchant';
    }
  };

  const getUserTypeIcon = (type: ParticipantType) => {
    switch (type) {
      case 'dispatcher': return '🚴';
      case 'customer': return '👤';
      case 'merchant': return '🏪';
    }
  };

  const handleSelectConversation = async (conversation: Conversation) => {
    try {
      const fullConversation = await communicationAPI.getConversation(conversation.id);
      setSelectedConversation(fullConversation);
      
      setConversations(prev => prev.map(c => 
        c.id === conversation.id ? { ...c, admin_unread_count: 0 } : c
      ));
    } catch (err: any) {
      setError(err.message || 'Failed to load conversation');
    }
  };

  const handleSendMessage = async () => {
    if (!selectedConversation || !newMessage.trim()) return;
    
    try {
      setSendingMessage(true);
      const message = await communicationAPI.sendMessage(selectedConversation.id, {
        content: newMessage.trim()
      });
      
      setSelectedConversation(prev => prev ? {
        ...prev,
        messages: [...prev.messages, message],
        last_message_preview: newMessage.trim().slice(0, 200),
        last_message_at: new Date().toISOString()
      } : null);
      
      setNewMessage('');
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStartConversation = async () => {
    if (!selectedUser || !initialMessage.trim()) return;
    
    try {
      setSendingMessage(true);
      const conversation = await communicationAPI.createConversation({
        participant_id: selectedUser.id,
        participant_type: selectedUser.user_type,
        subject: newSubject.trim() || undefined,
        initial_message: initialMessage.trim()
      });
      
      setConversations(prev => [conversation, ...prev]);
      setSelectedConversation(conversation);
      setShowNewConversation(false);
      setSelectedUser(null);
      setUserSearch('');
      setShowUserDropdown(false);
      setHighlightedIndex(-1);
      setNewSubject('');
      setInitialMessage('');
    } catch (err: any) {
      setError(err.message || 'Failed to start conversation');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSendBroadcast = async () => {
    if (!broadcastTitle.trim() || !broadcastMessage.trim()) return;
    
    try {
      setSendingBroadcast(true);
      const announcement = await communicationAPI.sendAnnouncement({
        title: broadcastTitle.trim(),
        message: broadcastMessage.trim(),
        announcement_type: broadcastType,
        priority: broadcastPriority,
        target_audience: targetAudience,
        send_push: sendPush,
        send_email: sendEmail,
        send_sms: sendSms
      });
      
      setAnnouncements(prev => [announcement, ...prev]);
      setBroadcastTitle('');
      setBroadcastMessage('');
      setBroadcastType('general');
      setBroadcastPriority('normal');
      setTargetAudience('all_dispatchers');
      setSendPush(true);
      setSendEmail(false);
      setSendSms(false);
      setActiveTab('announcements');
    } catch (err: any) {
      setError(err.message || 'Failed to send broadcast');
    } finally {
      setSendingBroadcast(false);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const filteredConversations = conversations.filter(c => 
    c.dispatcher_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.dispatcher_phone?.includes(searchTerm) ||
    c.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 p-4 bg-white border-b">
          <div className="text-center p-3 bg-sky-50 rounded-lg">
            <div className="text-2xl font-bold text-sky-600">{stats.total_conversations}</div>
            <div className="text-xs text-gray-500">Total Chats</div>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{stats.active_conversations}</div>
            <div className="text-xs text-gray-500">Active</div>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{stats.unread_messages}</div>
            <div className="text-xs text-gray-500">Unread</div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{stats.total_announcements}</div>
            <div className="text-xs text-gray-500">Broadcasts</div>
          </div>
          <div className="text-center p-3 bg-yellow-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending_announcements}</div>
            <div className="text-xs text-gray-500">Scheduled</div>
          </div>
          <div className="text-center p-3 bg-indigo-50 rounded-lg">
            <div className="text-2xl font-bold text-indigo-600">{stats.total_recipients_reached}</div>
            <div className="text-xs text-gray-500">Recipients</div>
          </div>
        </div>
      )}

      <div className="flex border-b bg-white">
        <button
          onClick={() => setActiveTab('conversations')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'conversations'
              ? 'text-sky-600 border-b-2 border-sky-600 bg-sky-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Conversations
          {(stats?.unread_messages ?? 0) > 0 && (
            <span className="ml-2 bg-orange-500 text-white text-xs rounded-full px-2 py-0.5">
              {stats?.unread_messages}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('announcements')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'announcements'
              ? 'text-sky-600 border-b-2 border-sky-600 bg-sky-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
          </svg>
          Announcements
        </button>
        <button
          onClick={() => setActiveTab('broadcast')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'broadcast'
              ? 'text-sky-600 border-b-2 border-sky-600 bg-sky-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
          New Broadcast
        </button>
        <button
          onClick={() => setActiveTab('statistics')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'statistics'
              ? 'text-sky-600 border-b-2 border-sky-600 bg-sky-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Statistics
        </button>
      </div>

      <div className="flex-1 overflow-hidden">
        {activeTab === 'conversations' && (
          <div className="flex h-full">
            <div className="w-80 border-r bg-white flex flex-col">
              <div className="p-3 border-b">
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Search conversations..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    />
                    <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <button
                    onClick={() => {
                      setShowNewConversation(true);
                      loadUsersByType(selectedUserType);
                    }}
                    className="p-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
                    title="New Conversation"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {filteredConversations.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-sm">No conversations yet</p>
                    <button
                      onClick={() => {
                        setShowNewConversation(true);
                        loadUsersByType(selectedUserType);
                      }}
                      className="mt-2 text-sky-600 text-sm hover:underline"
                    >
                      Start a new conversation
                    </button>
                  </div>
                ) : (
                  filteredConversations.map(conv => (
                    <div
                      key={conv.id}
                      onClick={() => handleSelectConversation(conv)}
                      className={`p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-sky-50 border-l-4 border-l-sky-500' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 overflow-hidden">
                            {conv.participant_photo ? (
                              <img src={conv.participant_photo} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-500 font-medium">
                                {conv.participant_name?.charAt(0) || conv.dispatcher_name?.charAt(0) || 'U'}
                              </div>
                            )}
                          </div>
                          <span className="absolute -bottom-1 -right-1 text-xs bg-white rounded-full shadow">
                            {getUserTypeIcon(conv.participant_type || 'dispatcher')}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className={`font-medium text-sm ${conv.admin_unread_count > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                              {conv.participant_name || conv.dispatcher_name || 'Unknown'}
                            </span>
                            <span className="text-xs text-gray-400">
                              {conv.last_message_at ? formatTime(conv.last_message_at) : ''}
                            </span>
                          </div>
                          {conv.subject && (
                            <div className="text-xs text-sky-600 truncate">{conv.subject}</div>
                          )}
                          <div className={`text-xs truncate ${conv.admin_unread_count > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                            {conv.last_message_by === 'admin' && <span className="text-gray-400">You: </span>}
                            {conv.last_message_preview || 'No messages yet'}
                          </div>
                        </div>
                        {conv.admin_unread_count > 0 && (
                          <span className="bg-sky-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                            {conv.admin_unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex-1 flex flex-col bg-gray-100">
              {selectedConversation ? (
                <>
                  <div className="bg-white border-b p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden">
                        {selectedConversation.dispatcher_photo ? (
                          <img src={selectedConversation.dispatcher_photo} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-500 font-medium">
                            {selectedConversation.dispatcher_name?.charAt(0) || 'D'}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{selectedConversation.dispatcher_name}</div>
                        <div className="text-xs text-gray-500">{selectedConversation.dispatcher_phone}</div>
                      </div>
                      {selectedConversation.subject && (
                        <div className="ml-auto text-sm text-gray-500">
                          {selectedConversation.subject}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {selectedConversation.messages.map(msg => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs md:max-w-md rounded-2xl px-4 py-2 ${
                            msg.sender_type === 'admin'
                              ? 'bg-sky-500 text-white rounded-br-sm'
                              : 'bg-white text-gray-900 rounded-bl-sm shadow-sm'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <div className={`text-xs mt-1 ${msg.sender_type === 'admin' ? 'text-sky-100' : 'text-gray-400'}`}>
                            {formatTime(msg.created_at)}
                            {msg.sender_type === 'admin' && msg.is_read && (
                              <svg className="w-3 h-3 inline ml-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="bg-white border-t p-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-full focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                        disabled={sendingMessage}
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={sendingMessage || !newMessage.trim()}
                        className="p-2 bg-sky-500 text-white rounded-full hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {sendingMessage ? (
                          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-lg font-medium">Select a conversation</p>
                    <p className="text-sm">Choose a dispatcher to start chatting</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'announcements' && (
          <div className="p-6 overflow-y-auto h-full bg-gradient-to-br from-gray-50 to-slate-100">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Announcement History</h2>
                  <p className="text-sm text-gray-500 mt-1">View all past broadcasts and their performance</p>
                </div>
                <button
                  onClick={() => setActiveTab('broadcast')}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium shadow-sm"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Broadcast
                </button>
              </div>

              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="bg-purple-100 rounded-lg p-2">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-900">{stats.total_announcements}</div>
                        <div className="text-xs text-gray-500">Total Broadcasts</div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 rounded-lg p-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-900">{stats.total_recipients_reached}</div>
                        <div className="text-xs text-gray-500">Total Reach</div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 rounded-lg p-2">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-900">
                          {stats.total_announcements > 0 ? Math.round(stats.total_recipients_reached / stats.total_announcements) : 0}
                        </div>
                        <div className="text-xs text-gray-500">Avg. per Broadcast</div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="bg-yellow-100 rounded-lg p-2">
                        <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-900">{stats.pending_announcements}</div>
                        <div className="text-xs text-gray-500">Scheduled</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {announcements.length === 0 ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No announcements yet</h3>
                    <p className="text-gray-500 mb-4">Start engaging with your dispatchers by sending your first broadcast</p>
                    <button
                      onClick={() => setActiveTab('broadcast')}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create First Broadcast
                    </button>
                  </div>
                ) : (
                  announcements.map(announcement => (
                    <div key={announcement.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                              announcement.priority === 'urgent' ? 'bg-red-100' :
                              announcement.priority === 'high' ? 'bg-orange-100' :
                              'bg-purple-100'
                            }`}>
                              <svg className={`w-6 h-6 ${
                                announcement.priority === 'urgent' ? 'text-red-600' :
                                announcement.priority === 'high' ? 'text-orange-600' :
                                'text-purple-600'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                                  announcement.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                                  announcement.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {announcement.priority.charAt(0).toUpperCase() + announcement.priority.slice(1)}
                                </span>
                                <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-sky-100 text-sky-700">
                                  {announcement.announcement_type.charAt(0).toUpperCase() + announcement.announcement_type.slice(1)}
                                </span>
                                <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                                  announcement.status === 'sent' ? 'bg-green-100 text-green-700' :
                                  announcement.status === 'scheduled' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {announcement.status.charAt(0).toUpperCase() + announcement.status.slice(1)}
                                </span>
                              </div>
                              <h3 className="font-semibold text-gray-900 text-lg">{announcement.title}</h3>
                              <p className="text-gray-600 mt-1.5 line-clamp-2">{announcement.message}</p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-sm font-medium text-gray-900">
                              {announcement.sent_at ? formatTime(announcement.sent_at) : formatTime(announcement.created_at)}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              by {announcement.admin_name || 'Admin'}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1.5 text-sm">
                              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                                <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              </div>
                              <span className="text-gray-600">
                                <span className="font-semibold text-gray-900">{announcement.delivered_count}</span>
                                <span className="text-gray-400"> / {announcement.total_recipients}</span> delivered
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 text-sm">
                              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                                <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </div>
                              <span className="text-gray-600">
                                <span className="font-semibold text-gray-900">{announcement.read_count}</span> read
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {announcement.send_push && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-md">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                                Push
                              </span>
                            )}
                            {announcement.send_email && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-md">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Email
                              </span>
                            )}
                            {announcement.send_sms && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-rose-50 text-rose-700 text-xs font-medium rounded-md">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                                SMS
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'broadcast' && (
          <div className="p-4 overflow-y-auto h-full">
            <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New Broadcast</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={broadcastTitle}
                    onChange={(e) => setBroadcastTitle(e.target.value)}
                    placeholder="e.g., Important Update: New Delivery Guidelines"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={broadcastMessage}
                    onChange={(e) => setBroadcastMessage(e.target.value)}
                    placeholder="Enter your message here..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={broadcastType}
                      onChange={(e) => setBroadcastType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    >
                      <option value="general">General</option>
                      <option value="update">Update</option>
                      <option value="promotion">Promotion</option>
                      <option value="alert">Alert</option>
                      <option value="policy">Policy Change</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      value={broadcastPriority}
                      onChange={(e) => setBroadcastPriority(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                  <select
                    value={targetAudience}
                    onChange={(e) => setTargetAudience(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  >
                    <option value="all_dispatchers">All Dispatchers</option>
                    <option value="all_customers">All Customers</option>
                    <option value="all_merchants">All Merchants</option>
                    <option value="all_users">All Users</option>
                    <option value="by_status">By Status (Approved Only)</option>
                    <option value="by_vehicle">By Vehicle Type</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Channels</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendPush}
                        onChange={(e) => setSendPush(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                      />
                      <span className="text-sm text-gray-700">Push Notification</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                      />
                      <span className="text-sm text-gray-700">Email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendSms}
                        onChange={(e) => setSendSms(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                      />
                      <span className="text-sm text-gray-700">SMS</span>
                    </label>
                  </div>
                </div>

                <button
                  onClick={handleSendBroadcast}
                  disabled={sendingBroadcast || !broadcastTitle.trim() || !broadcastMessage.trim()}
                  className="w-full py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {sendingBroadcast ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Broadcast
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'statistics' && stats && (
          <div className="h-full overflow-y-auto p-6">
            <div className="max-w-6xl mx-auto space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Communication Analytics Overview
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-sky-50 to-sky-100 rounded-xl p-4 border border-sky-200">
                    <div className="flex items-center justify-between mb-2">
                      <svg className="w-8 h-8 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <span className="text-3xl font-bold text-sky-600">{stats.total_conversations}</span>
                    </div>
                    <p className="text-sm font-medium text-sky-700">Total Conversations</p>
                    <p className="text-xs text-sky-600 mt-1">All admin-dispatcher chats</p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-3xl font-bold text-green-600">{stats.active_conversations}</span>
                    </div>
                    <p className="text-sm font-medium text-green-700">Active Conversations</p>
                    <p className="text-xs text-green-600 mt-1">Currently ongoing chats</p>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                    <div className="flex items-center justify-between mb-2">
                      <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="text-3xl font-bold text-orange-600">{stats.unread_messages}</span>
                    </div>
                    <p className="text-sm font-medium text-orange-700">Unread Messages</p>
                    <p className="text-xs text-orange-600 mt-1">Pending admin response</p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <svg className="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                      </svg>
                      <span className="text-3xl font-bold text-purple-600">{stats.total_announcements}</span>
                    </div>
                    <p className="text-sm font-medium text-purple-700">Total Announcements</p>
                    <p className="text-xs text-purple-600 mt-1">Broadcasts sent</p>
                  </div>

                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200">
                    <div className="flex items-center justify-between mb-2">
                      <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-3xl font-bold text-yellow-600">{stats.pending_announcements}</span>
                    </div>
                    <p className="text-sm font-medium text-yellow-700">Scheduled</p>
                    <p className="text-xs text-yellow-600 mt-1">Pending announcements</p>
                  </div>

                  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border border-indigo-200">
                    <div className="flex items-center justify-between mb-2">
                      <svg className="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span className="text-3xl font-bold text-indigo-600">{stats.total_recipients_reached}</span>
                    </div>
                    <p className="text-sm font-medium text-indigo-700">Recipients Reached</p>
                    <p className="text-xs text-indigo-600 mt-1">Total announcement views</p>
                  </div>

                  <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-4 border border-teal-200">
                    <div className="flex items-center justify-between mb-2">
                      <svg className="w-8 h-8 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-3xl font-bold text-teal-600">
                        {stats.total_announcements > 0 
                          ? Math.round((stats.total_recipients_reached / Math.max(stats.total_announcements, 1)) * 10) / 10
                          : 0}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-teal-700">Avg. Reach</p>
                    <p className="text-xs text-teal-600 mt-1">Recipients per broadcast</p>
                  </div>

                  <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-xl p-4 border border-rose-200">
                    <div className="flex items-center justify-between mb-2">
                      <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="text-3xl font-bold text-rose-600">
                        {stats.active_conversations > 0 ? 'Active' : 'Idle'}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-rose-700">System Status</p>
                    <p className="text-xs text-rose-600 mt-1">Communication flow</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Conversation Insights
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Total Conversations</span>
                      <span className="font-semibold text-gray-900">{stats.total_conversations}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-sm text-green-700">Active Threads</span>
                      <span className="font-semibold text-green-600">{stats.active_conversations}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <span className="text-sm text-orange-700">Awaiting Response</span>
                      <span className="font-semibold text-orange-600">{stats.unread_messages}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Response Rate</span>
                      <span className="font-semibold text-gray-900">
                        {stats.total_conversations > 0 
                          ? `${Math.round(((stats.total_conversations - stats.unread_messages) / stats.total_conversations) * 100)}%`
                          : '0%'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                    </svg>
                    Broadcast Performance
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Total Broadcasts</span>
                      <span className="font-semibold text-gray-900">{stats.total_announcements}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <span className="text-sm text-purple-700">Total Recipients</span>
                      <span className="font-semibold text-purple-600">{stats.total_recipients_reached}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <span className="text-sm text-yellow-700">Scheduled</span>
                      <span className="font-semibold text-yellow-600">{stats.pending_announcements}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Avg. Reach per Broadcast</span>
                      <span className="font-semibold text-gray-900">
                        {stats.total_announcements > 0 
                          ? Math.round(stats.total_recipients_reached / stats.total_announcements)
                          : 0} users
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Quick Actions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    onClick={() => setActiveTab('conversations')}
                    className="flex items-center justify-center gap-2 p-4 bg-sky-50 hover:bg-sky-100 rounded-lg text-sky-700 font-medium transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    View Conversations
                  </button>
                  <button
                    onClick={() => setActiveTab('broadcast')}
                    className="flex items-center justify-center gap-2 p-4 bg-orange-50 hover:bg-orange-100 rounded-lg text-orange-700 font-medium transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Send New Broadcast
                  </button>
                  <button
                    onClick={() => setActiveTab('announcements')}
                    className="flex items-center justify-center gap-2 p-4 bg-purple-50 hover:bg-purple-100 rounded-lg text-purple-700 font-medium transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                    </svg>
                    View Announcement History
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showNewConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">New Conversation</h3>
              <button
                onClick={() => {
                  setShowNewConversation(false);
                  setSelectedUser(null);
                  setUserSearch('');
                  setShowUserDropdown(false);
                  setHighlightedIndex(-1);
                  setNewSubject('');
                  setInitialMessage('');
                }}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">User Type</label>
                <div className="flex gap-2">
                  {(['dispatcher', 'customer', 'merchant'] as ParticipantType[]).map((type) => (
                    <button
                      key={type}
                      onClick={() => handleUserTypeChange(type)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        selectedUserType === type
                          ? 'bg-sky-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {getUserTypeIcon(type)} {getUserTypeLabel(type)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select {getUserTypeLabel(selectedUserType)}</label>
                
                {selectedUser ? (
                  <div className="flex items-center justify-between p-3 bg-sky-50 border border-sky-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      {selectedUser.photo ? (
                        <img 
                          src={selectedUser.photo} 
                          alt={selectedUser.name}
                          className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-sky-200 flex items-center justify-center">
                          <span className="text-sky-700 font-semibold text-sm">
                            {selectedUser.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{selectedUser.name}</p>
                        <p className="text-sm text-gray-500">{selectedUser.phone}</p>
                      </div>
                    </div>
                    <button
                      onClick={handleClearUser}
                      className="p-1.5 hover:bg-sky-100 rounded-full transition-colors"
                      title="Clear selection"
                    >
                      <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="relative" ref={dropdownRef}>
                    <div className="relative">
                      <input
                        ref={userInputRef}
                        type="text"
                        value={userSearch}
                        onChange={(e) => {
                          setUserSearch(e.target.value);
                          setShowUserDropdown(true);
                          setHighlightedIndex(-1);
                        }}
                        onFocus={() => setShowUserDropdown(true)}
                        onBlur={() => {
                          setTimeout(() => setShowUserDropdown(false), 200);
                        }}
                        onKeyDown={handleUserKeyDown}
                        placeholder={`Type to search ${getUserTypeLabel(selectedUserType).toLowerCase()}s...`}
                        className="w-full px-3 py-2.5 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      />
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    
                    {showUserDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredUsers.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            {userSearch ? `No ${getUserTypeLabel(selectedUserType).toLowerCase()}s found` : 'Start typing to search...'}
                          </div>
                        ) : (
                          filteredUsers.map((user, index) => (
                            <button
                              key={user.id}
                              onClick={() => handleUserSelect(user)}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left ${
                                index === highlightedIndex ? 'bg-sky-50' : ''
                              } ${index !== filteredUsers.length - 1 ? 'border-b border-gray-100' : ''}`}
                            >
                              {user.photo ? (
                                <img 
                                  src={user.photo} 
                                  alt={user.name}
                                  className="w-9 h-9 rounded-full object-cover border border-gray-200"
                                />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-gray-600 font-medium text-xs">
                                    {user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">{user.name}</p>
                                <p className="text-sm text-gray-500 truncate">{user.phone}</p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {!selectedUser && users.length > 0 && !showUserDropdown && (
                  <p className="mt-1.5 text-xs text-gray-500">
                    {users.length} {getUserTypeLabel(selectedUserType).toLowerCase()}{users.length !== 1 ? 's' : ''} available
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject (optional)</label>
                <input
                  type="text"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  placeholder="e.g., Performance Review, Schedule Update"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Initial Message</label>
                <textarea
                  value={initialMessage}
                  onChange={(e) => setInitialMessage(e.target.value)}
                  placeholder="Write your first message..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-none"
                />
              </div>

              <button
                onClick={handleStartConversation}
                disabled={sendingMessage || !selectedUser || !initialMessage.trim()}
                className="w-full py-2 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sendingMessage ? 'Starting...' : 'Start Conversation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCommunicationHub;
